<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useChannelsStore } from '@state/stores/channels'
import { SOCIAL_SHARE_IMAGE, usePageHead } from '@composables/use-page-head'
import { useRoute } from 'vue-router'
import DataLayer from '@services/data-layer/index'
import ShowScheduleBlock from '@components/show-schedule-block'
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'
import startOfToday from 'date-fns/startOfToday'

const DAYS_AHEAD = 7
const PLACEHOLDER_ROWS = 6

const route = useRoute()

const dates = ref([])
const activeDate = ref({})
const schedule = ref(
    Array.from(Array(PLACEHOLDER_ROWS), (_row, id) => ({
        id,
        show: {
            id,
            title: '',
            url: '',
            type: 'show',
            image: {},
            genre: [],
            artist_id: [],
            artist: [],
        },
    }))
)

const activeDateLabel = computed(() => activeDate.value.label || '')

const dateLabels = computed(() => dates.value.map(date => date.label))

function getChannelSchedule(date) {
    DataLayer.episodes
        .getByDate(route.params.channelId, date, addDays(date, 1))
        .then(episodes => {
            schedule.value = episodes.body
        })
}

function handleDateChange(label) {
    activeDate.value = dates.value.find(date => date.label === label)
}

watch(activeDate, date => {
    if (!date.date) return

    getChannelSchedule(date.date)
})

onMounted(() => {
    const today = startOfToday()

    dates.value = Array.from(Array(DAYS_AHEAD), (_day, offset) => {
        const date = addDays(today, offset)
        return { label: format(date, 'd MMM yyyy'), date }
    })

    activeDate.value = dates.value[0]
})

usePageHead(computed(() => {
    const channel = useChannelsStore().channels
        .find(item => item.id === route.params.channelId) || {}
    const channelTitle = channel.title || 'Channel'

    return {
        title: `Schedule — ${channelTitle}`,
        description: channel.description || `Upcoming show schedule for ${channelTitle}.`,
        image: SOCIAL_SHARE_IMAGE,
    }
}))
</script>

<template>
    <BaseContent>
        <BaseSection
            title="Schedule"
            padded
        >
            <template #right>
                <div>
                    <BaseCustomSelect
                        :options="dateLabels"
                        :active="activeDateLabel"
                        prefix="Date:"
                        @change="handleDateChange"
                    />
                </div>
            </template>
            <p class="timezone-note">
                All times shown in your local timezone
            </p>
            <ShowScheduleBlock
                :resources="schedule"
                :error="{ paragraph: 'There are no shows scheduled for this day' }"
            />
        </BaseSection>
    </BaseContent>
</template>

<style lang="scss" scoped>
@import '@design';

.timezone-note {
    @extend %contentSubtitleS;

    margin: 0 0 $s-m;
    color: $c-shuttle-gray;
}
</style>
